import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  IClaim,
  IClaimDetail,
  IClaimDetailItem,
  INewClaimLine,
  IStoreState,
} from '../../../types';
import { FullPageLayout } from '../../../layouts';
import { Button, Card } from '@orderly/morrisons-component-library';
import { SkeletonLoader } from '../../../components';
import { toastr } from 'react-redux-toastr';
import OrderInfoCard from '../../../components/OrderInfoCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getClaimDetail, ISubmitClaimBody, submitClaim } from '../../../api/MyClaims';
import { navigateTo } from '../../../routing/Navigation';
import { QUERY_KEYS } from '../../../constants';
import { ClaimDetailsTable } from './components';
import styles from './DhlClaimDetail.module.scss';
import FormatDateHelper from '../../../helpers/Format/Date/FormatDateHelper';
import { IDateValues } from '../../../helpers/Format/Date/IDateValues';
import { mapCategory } from '../../../helpers/Category/mapCategory';

interface IDhlClaimDetailProps {
  match: {
    params: {
      orderId: string;
    };
  };
}

const DhlClaimDetail = ({
  match,
}: Readonly<IDhlClaimDetailProps>) => {
  const orderId = match.params.orderId;
  const [orderDay, setOrderDay] = useState('');
  const [orderMonth, setOrderMonth] = useState('');
  const [newLines, setNewLines] = useState<{ [key: string]: INewClaimLine[] }>({});
  const mutation = useMutation<IClaim, Error, ISubmitClaimBody>(submitClaim, {
    onSuccess: (data: IClaim) => {
      toastr.success(
        'Claim Created',
        `Your claim has been created ${data.reference}`,
      );
      navigateTo('/orders/my-claims');
    },
  });

  const baseData: IClaimDetail = {
    order: null,
    items: [],
    canClaim: false,
  };

  const { data: claimDetail, isLoading } = useQuery<IClaimDetail, Error>(
    [QUERY_KEYS.CLAIM_DETAIL, orderId],
    () => getClaimDetail({
      orderNumber: orderId,
    }),
    {
      initialData: baseData,
    },
  );

  const { order, items, canClaim } = claimDetail;

  useEffect(() => {
    if (order) {
      const dateValues: IDateValues = FormatDateHelper.getDateValues(order.alternativeDeliverAt || order.deliverAt);

      setOrderDay(dateValues.date);
      setOrderMonth(dateValues.month);
    }
  }, [order]);

  const onSubmit = () => {
    const allClaimLines: INewClaimLine[] = [];
    items.forEach((item: IClaimDetailItem) => {
      const newItemLines = newLines[item.skuPin] || [];
      [...newItemLines].forEach((line) => {
        if (line.productsAffected > 0) {
          allClaimLines.push(line);
        }
      });
    });

    if (allClaimLines.length > 0) {
      mutation.mutate({
        orderNumber: order.orderId,
        lines: allClaimLines,
      });
    } else {
      toastr.warning(
        'No Applicatable claims',
        'Please add at least one claim line.',
      );
    }
  };

  return (
    <FullPageLayout
      heading={`Order #${orderId}`}
      breadcrumbs={[
        { key: 0, text: 'Orders', url: '/orders' },
        { key: 1, text: 'My Claims', url: '/orders/my-claims' },
        { key: 2, text: `Order #${orderId}`, url: window.location.pathname },
      ]}
      headerAdditionalContent={
        canClaim &&
        <Button
          className="tertiary"
          type="submit"
          text="Submit"
          onClick={onSubmit}
          disabled={mutation.isLoading}
        />
      }
    >
      <div className={styles.container}>
        <div className={styles.orderInfo}>
          {
            (!isLoading && order) ?
              <OrderInfoCard
                orderDay={orderDay}
                orderMonth={orderMonth}
                orderNumber={match.params.orderId}
              >
                <table className={styles.orderDetails}>
                  <tbody>
                    <tr>
                      <td>Order date</td>
                      <td>
                        {order && FormatDateHelper.formatShortDate(order.createdAt)}
                      </td>
                    </tr>
                    <tr>
                      <td>Delivery date</td>
                      <td>{order && FormatDateHelper.formatShortDate(order.alternativeDeliverAt || order.deliverAt)}</td>
                    </tr>
                    <tr>
                      <td>Items number</td>
                      <td>{items.length}</td>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>{order && mapCategory(order.categoryCode)}</td>
                    </tr>
                  </tbody>
                </table>
              </OrderInfoCard> :
              <Card
                className={styles.orderInfoLoading}
              >
                <SkeletonLoader />
              </Card>
          }
        </div>
        <div className={styles.content}>
          {
            (!isLoading && order) ?
              <ClaimDetailsTable
                items={items}
                newLines={newLines}
                setNewLines={setNewLines}
                canClaim={canClaim}
              /> :
              <Card
                className={styles.loadingCard}
              >
                <SkeletonLoader />
              </Card>
          }

        </div>
      </div>
    </FullPageLayout>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DhlClaimDetail);
